import React from 'react';
import { Box, Grid, Paper, Typography, Stack, Chip, Divider, Drawer, IconButton, Alert, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { AuditLogFilters } from '@/components/audit/AuditLogFilters';
import { AuditLogsTable } from '@/components/audit/AuditLogsTable';
import { AuditLogCharts } from '@/components/audit/AuditLogCharts';
import { AuditLogTimeline } from '@/components/audit/AuditLogTimeline';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const initialFilters = {
	email: '',
	eventAction: [],
	collection: [],
	operationType: '',
	startDate: null,
	endDate: null
};

const infoRow = (label, value) => (
	<Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="body2" fontWeight={600} sx={{ maxWidth: 280, textAlign: 'right' }}>
			{value || 'N/A'}
		</Typography>
	</Stack>
);

function DetailDrawer({ log, onClose }) {
	if (!log) return null;

	const pretty = value => JSON.stringify(value, null, 2);
	const timestamp = log.timestamp ? new Date(log.timestamp) : null;
	const tsLabel = timestamp && !Number.isNaN(timestamp.getTime()) ? format(timestamp, 'PPpp') : 'N/A';

	return (
		<Drawer
			anchor="right"
			open={Boolean(log)}
			onClose={onClose}
			PaperProps={{ sx: { width: { xs: '100%', sm: 420, md: 520 }, p: 3 } }}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
				<Stack spacing={0.5}>
					<Typography variant="h6" fontWeight={800}>
						{log.eventAction}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{tsLabel}
					</Typography>
				</Stack>
				<IconButton onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</Stack>

			<Stack direction="row" spacing={1} sx={{ mb: 1 }}>
				<Chip
					label={(log.operationType || '').toUpperCase()}
					color={log.operationType === 'delete' ? 'error' : 'primary'}
				/>
				<Chip label={log.actorType || 'unknown'} variant="outlined" />
				<Chip label={log.source || 'source'} variant="outlined" />
			</Stack>

			<Stack spacing={1.5} sx={{ mt: 1 }}>
				{infoRow('Collection', log.collection)}
				{infoRow('Document ID', log.documentId)}
				{infoRow('Updated By', log.updatedBy)}
				{infoRow('Database', log.database)}
				{infoRow('Prefix', log.prefix)}
				{infoRow('Source', log.source)}
			</Stack>

			<Divider sx={{ my: 2 }} />

			<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
				Changed Fields
			</Typography>
			<Box
				component="pre"
				sx={{
					p: 1.5,
					bgcolor: 'grey.50',
					borderRadius: 2,
					border: theme => `1px solid ${theme.palette.divider}`,
					maxHeight: 160,
					overflow: 'auto'
				}}
			>
				{pretty(log.changedFields || {})}
			</Box>

			<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
				Document (Before -&gt; After)
			</Typography>
			<Stack direction="row" spacing={1} sx={{ mb: 1, height: 220, overflow: 'auto' }}>
				<Box
					component="pre"
					sx={{
						flex: 1,
						p: 1.5,
						bgcolor: 'grey.50',
						borderRadius: 2,
						border: theme => `1px solid ${theme.palette.divider}`,
						overflow: 'auto'
					}}
				>
					{pretty(log.fullDocumentBefore || {})}
				</Box>
				<Box
					component="pre"
					sx={{
						flex: 1,
						p: 1.5,
						bgcolor: 'grey.50',
						borderRadius: 2,
						border: theme => `1px solid ${theme.palette.divider}`,
						overflow: 'auto'
					}}
				>
					{pretty(log.fullDocument || {})}
				</Box>
			</Stack>
		</Drawer>
	);
}

function AuditLogsPage() {
	const [page, setPage] = React.useState(1);
	const [pageSize, setPageSize] = React.useState(20);
	const [sortBy, setSortBy] = React.useState('timestamp');
	const [sortOrder, setSortOrder] = React.useState('desc');
	const [filters, setFilters] = React.useState(initialFilters);
	const [selectedLog, setSelectedLog] = React.useState(null);

	const { data, meta, isLoading, isError, error, mutate } = useAuditLogs({
		page,
		pageSize,
		sortBy,
		sortOrder,
		...filters
	});

	const logs = data || [];

	const stats = React.useMemo(() => {
		const total = meta?.total || logs.length || 0;
		const actors = logs.reduce(
			(acc, cur) => {
				const key = cur?.actorType || 'unknown';
				acc[key] = (acc[key] || 0) + 1;
				return acc;
			},
			{ user: 0, system: 0, unknown: 0 }
		);
		const collections = new Set(logs.map(item => item.collection).filter(Boolean)).size;
		const lastActivity = logs
			.map(item => item.timestamp)
			.filter(Boolean)
			.map(ts => new Date(ts))
			.filter(d => !Number.isNaN(d.getTime()))
			.sort((a, b) => b - a)?.[0];

		return {
			total,
			collections,
			lastActivity: lastActivity ? dayjs(lastActivity).format('MMM D, YYYY HH:mm') : 'N/A',
			actors
		};
	}, [logs, meta?.total]);

	const collectionOptions = React.useMemo(() => {
		const unique = new Set(logs.map(item => item.collection).filter(Boolean));
		return Array.from(unique);
	}, [logs]);

	const handleFiltersChange = next => {
		setFilters(next);
		setPage(1);
	};

	const handleSort = model => {
		setSortBy(model.field || 'timestamp');
		setSortOrder(model.sort || 'desc');
		setPage(1);
	};

	const applyQuickRange = days => {
		const end = new Date();
		const start = dayjs(end).subtract(days, 'day').toDate();
		setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
		setPage(1);
	};

	return (
		<Box
			sx={{
				py: 2,
				px: { xs: 1, md: 2, lg: 3 },
				maxWidth: 1440,
				mx: 'auto',
				bgcolor: 'background.default',
				minHeight: '100vh'
			}}
		>
			<Paper
				variant="outlined"
				sx={{
					p: { xs: 2, md: 3 },
					mb: 2,
					borderRadius: 3,
					boxShadow: '0 16px 80px rgba(0,0,0,0.05)',
					border: theme => `1px solid ${theme.palette.divider}`,
					background: theme =>
						`linear-gradient(135deg, ${theme.palette.primary.main}0d, ${theme.palette.secondary.main}0f), ${theme.palette.background.paper}`
				}}
			>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					alignItems={{ xs: 'flex-start', md: 'center' }}
					justifyContent="space-between"
					spacing={2}
				>
					<Box>
						<Typography variant="h4" fontWeight={800} gutterBottom>
							Audit Logs
						</Typography>
						<Typography variant="body1" color="text.secondary">
							Deep visibility into database changes, logins, and system events.
						</Typography>
					</Box>
					<Stack direction="row" spacing={1}>
						<Button startIcon={<RefreshIcon />} onClick={() => mutate()} variant="outlined" size="small">
							Refresh
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={() => {
								setFilters(initialFilters);
								setPage(1);
							}}
						>
							Clear Filters
						</Button>
					</Stack>
				</Stack>

				<Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
					<Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', pr: 1 }}>
						Quick ranges:
					</Typography>
					<Button size="small" variant="text" onClick={() => applyQuickRange(1)}>
						Last 24h
					</Button>
					<Button size="small" variant="text" onClick={() => applyQuickRange(7)}>
						Last 7d
					</Button>
					<Button size="small" variant="text" onClick={() => applyQuickRange(30)}>
						Last 30d
					</Button>
				</Stack>

				<Grid container spacing={2} sx={{ mt: 2 }}>
					<Grid item xs={12} sm={6} md={3}>
						<Paper
							variant="outlined"
							sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', borderColor: 'divider', height: '100%' }}
						>
							<Typography variant="subtitle2" color="text.secondary">
								Total Events
							</Typography>
							<Typography variant="h5" fontWeight={800}>
								{stats.total}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
							<Typography variant="subtitle2" color="text.secondary">
								Collections Seen
							</Typography>
							<Typography variant="h5" fontWeight={800}>
								{stats.collections}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
							<Typography variant="subtitle2" color="text.secondary">
								Last Activity
							</Typography>
							<Typography variant="h6" fontWeight={700}>
								{stats.lastActivity}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
							<Typography variant="subtitle2" color="text.secondary">
								Actors Mix
							</Typography>
							<Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
								<Chip size="small" label={`User ${stats.actors.user || 0}`} color="primary" />
								<Chip size="small" label={`System ${stats.actors.system || 0}`} color="info" />
								<Chip size="small" label={`Unknown ${stats.actors.unknown || 0}`} color="warning" />
							</Stack>
						</Paper>
					</Grid>
				</Grid>
			</Paper>

			<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
				<AuditLogFilters filters={filters} onChange={handleFiltersChange} collections={collectionOptions} />
			</motion.div>

			{isError && (
				<Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => mutate()}>Retry</Button>}>
					Failed to load audit logs: {error?.message || 'Unknown error'}
				</Alert>
			)}

			<Stack spacing={2}>
				<Paper
					variant="outlined"
					sx={{
						p: 2,
						borderRadius: 3,
						bgcolor: 'background.paper',
						boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
						overflow: 'hidden'
					}}
				>
					{!isLoading && !logs.length ? (
						<Box sx={{ p: 4, textAlign: 'center' }}>
							<Typography variant="h6" fontWeight={700}>
								No audit events found
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								Adjust filters or refresh to load the latest activity.
							</Typography>
						</Box>
					) : (
						<AuditLogsTable
							rows={logs}
							meta={meta}
							loading={isLoading}
							sortBy={sortBy}
							sortOrder={sortOrder}
							onPageChange={setPage}
							onPageSizeChange={setPageSize}
							onSortModelChange={handleSort}
							onRowClick={row => setSelectedLog(row)}
						/>
					)}
				</Paper>

				<Paper
					variant="outlined"
					sx={{
						p: 2,
						borderRadius: 3,
						bgcolor: 'background.paper',
						boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
						overflow: 'hidden'
					}}
				>
					<AuditLogCharts logs={logs} loading={isLoading} />
				</Paper>

				<AuditLogTimeline logs={logs} />
			</Stack>

			<DetailDrawer log={selectedLog} onClose={() => setSelectedLog(null)} />
		</Box>
	);
}

export default AuditLogsPage;
